import { useState } from "react";
import { Check, Copy, Download, Send, X } from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { CONTACT_URL } from "@/config/product-links";
import { siteXUrl } from "@/config/site";
import { productUrl } from "@/lib/product-attribution";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useTranslation } from "@/common/providers/i18n-provider";
import type { ImageSizes } from "@/lib/image-sizes";
import "@/styles/company-pages.css";

/** The only images this page renders; verified against the generated catalog. */
const IMAGE_SIZES = {
  "/img/favicon.png": { width: 256, height: 256 },
  "/img/favicon.svg": { width: 512, height: 512 },
  "/img/beancount-logo+text.png": { width: 1764, height: 240 },
} satisfies ImageSizes;

/** Official asset bytes and palette stay unchanged; this page is their library. */
const BRAND_COLORS = [
  ["brand.colors.green", "#5FC535"],
  ["brand.colors.deepGreen", "#307D04"],
  ["brand.colors.forest", "#215C01"],
  ["brand.colors.bone", "#F1EFE4"],
  ["brand.colors.stone", "#6B6E5F"],
  ["brand.colors.charcoal", "#171A14"],
] as const;

function AssetCard({
  title,
  description,
  alt,
  src,
  downloadName,
  wordmark = false,
}: {
  title: string;
  description: string;
  alt: string;
  src: keyof typeof IMAGE_SIZES;
  /** Stable ASCII filename, never derived from a translated title. */
  downloadName: string;
  wordmark?: boolean;
}) {
  const { t } = useTranslation();
  const dimensions = IMAGE_SIZES[src];
  return (
    <article
      className={wordmark ? "brand-asset brand-asset-wordmark" : "brand-asset"}
    >
      <div className="brand-asset-preview">
        <img
          src={src}
          alt={alt}
          width={dimensions.width}
          height={dimensions.height}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="brand-asset-info">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
          <code dir="ltr">
            {dimensions.width} × {dimensions.height}
          </code>
        </div>
        <a href={src} download={downloadName} className="brand-download">
          <Download size={18} aria-hidden="true" />
          {t("brand.card.download")}
          <span className="sr-only">: {title}</span>
        </a>
      </div>
    </article>
  );
}

function Guideline({
  type,
  title,
  description,
}: {
  type: "do" | "dont";
  title: string;
  description: string;
}) {
  const Icon = type === "do" ? Check : X;
  return (
    <li>
      <Icon size={19} aria-hidden="true" />
      <div>
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </li>
  );
}

export function BrandAssetsComponent() {
  const { t } = useTranslation();
  const [copyResult, setCopyResult] = useState<{
    hex: string;
    ok: boolean;
  } | null>(null);
  const contactUrl = productUrl(CONTACT_URL, "brand_assets");

  async function copyColor(hex: string) {
    const ok = await copyTextToClipboard(hex);
    setCopyResult({ hex, ok });
  }

  // Keep the technical value left-to-right inside translated RTL feedback.
  // Each catalog preserves exactly one {hex} slot, checked by the locale tests.
  const copyMessage = copyResult
    ? t(copyResult.ok ? "brand.copy.success" : "brand.copy.failure").split(
        "{hex}",
      )
    : [];

  return (
    <div className="company-page brand-page">
      <AppHeader />
      <main id="main">
        <section className="company-container brand-hero">
          <div>
            <h1>{t("brand.brandAssetsGuidelines")}</h1>
            <p className="company-lead">{t("brand.hero.subtitle")}</p>
            <nav
              className="brand-nav"
              aria-label={t("brand.brandAssetsGuidelines")}
            >
              <a href="#logos">{t("brand.logos.h2")}</a>
              <a href="#colors">{t("brand.colors.h2")}</a>
              <a href="#typography">{t("brand.typography.h2")}</a>
              <a href="#guidelines">{t("brand.guidelines.h2")}</a>
            </nav>
          </div>
          <div className="brand-signature">
            <img
              src="/img/favicon.svg"
              alt={t("brand.overview.logoAlt")}
              width={IMAGE_SIZES["/img/favicon.svg"].width}
              height={IMAGE_SIZES["/img/favicon.svg"].height}
              decoding="async"
            />
            <p className="brand-signature-name">{t("brand.beancountIo")}</p>
            <p>{t("brand.overview.tagline")}</p>
          </div>
        </section>

        <section
          className="company-container brand-section"
          id="logos"
          aria-labelledby="brand-logos-title"
        >
          <h2 id="brand-logos-title">{t("brand.logos.h2")}</h2>
          <div className="brand-assets-grid">
            <AssetCard
              title={t("brand.logos.iconPng.title")}
              description={t("brand.logos.iconPng.desc")}
              alt={t("brand.logos.iconPng.alt")}
              src="/img/favicon.png"
              downloadName="beancount-icon.png"
            />
            <AssetCard
              title={t("brand.logos.iconSvg.title")}
              description={t("brand.logos.iconSvg.desc")}
              alt={t("brand.logos.iconSvg.alt")}
              src="/img/favicon.svg"
              downloadName="beancount-icon.svg"
            />
            <AssetCard
              title={t("brand.logos.logoText.title")}
              description={t("brand.logos.logoText.desc")}
              alt={t("brand.logos.logoText.alt")}
              src="/img/beancount-logo+text.png"
              downloadName="beancount-logo-text.png"
              wordmark
            />
          </div>
          <details className="brand-requests">
            <summary>{t("brand.logos.more.h3")}</summary>
            <p>{t("brand.logos.more.body")}</p>
            <ul>
              <li>{t("brand.logos.more.item.print")}</li>
              <li>{t("brand.logos.more.item.monochrome")}</li>
              <li>{t("brand.logos.more.item.dimensions")}</li>
              <li>{t("brand.logos.more.item.layouts")}</li>
            </ul>
            <a href={contactUrl} className="company-text-link">
              {t("brand.logos.more.cta")}
            </a>
          </details>
        </section>

        <section
          className="company-container brand-section"
          id="colors"
          aria-labelledby="brand-colors-title"
        >
          <h2 id="brand-colors-title">{t("brand.colors.h2")}</h2>
          <div className="brand-colors">
            {BRAND_COLORS.map(([key, hex]) => (
              <div className="brand-color" key={hex}>
                <div
                  className="brand-color-sample"
                  style={{ backgroundColor: hex }}
                />
                <h3>{t(key)}</h3>
                <div className="brand-color-value">
                  <code dir="ltr">{hex}</code>
                  <button
                    type="button"
                    onClick={() => void copyColor(hex)}
                    aria-label={t("brand.copy.label", { hex })}
                    title={t("brand.copy.label", { hex })}
                  >
                    {copyResult?.hex === hex && copyResult.ok ? (
                      <Check size={18} aria-hidden="true" />
                    ) : (
                      <Copy size={18} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p role="status" className="brand-copy-status">
            {copyResult ? (
              <>
                {copyMessage[0]}
                <bdi dir="ltr">{copyResult.hex}</bdi>
                {copyMessage[1]}
              </>
            ) : null}
          </p>
        </section>

        <section
          className="brand-type-band"
          id="typography"
          aria-labelledby="brand-type-title"
        >
          <div className="company-container">
            <h2 id="brand-type-title">{t("brand.typography.h2")}</h2>
            <div className="brand-type">
              <div>
                <h3>{t("brand.typography.primary.h3")}</h3>
                <p className="brand-type-family">
                  {t("brand.typography.primary.link")}
                </p>
                {/* The documented CSS value, not translatable prose. A regression
                    test compares it to the actual --font-grotesk token. */}
                <code className="brand-font-stack" dir="ltr">
                  -apple-system, "Segoe UI", "Helvetica Neue", system-ui,
                  sans-serif
                </code>
              </div>
              <div className="brand-type-samples">
                <h3>{t("brand.typography.samples.h3")}</h3>
                <p>{t("brand.typography.samples.headingBold")}</p>
                <p>{t("brand.typography.samples.subheadingSemibold")}</p>
                <p>{t("brand.typography.samples.bodyRegular")}</p>
                <p>{t("brand.typography.samples.captionSmall")}</p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="company-container brand-overview"
          aria-labelledby="brand-overview-title"
        >
          <h2 id="brand-overview-title">{t("brand.overview.h2")}</h2>
          <div>
            <p className="company-lead">{t("brand.overview.body")}</p>
            <ul>
              <li>{t("brand.overview.trait.modern")}</li>
              <li>{t("brand.overview.trait.transparent")}</li>
              <li>{t("brand.overview.trait.developer")}</li>
            </ul>
          </div>
        </section>

        <section
          className="company-container brand-section"
          id="guidelines"
          aria-labelledby="brand-guidelines-title"
        >
          <h2 id="brand-guidelines-title">{t("brand.guidelines.h2")}</h2>
          <div className="brand-guidelines">
            <div>
              <h3>{t("brand.guidelines.dos.h3")}</h3>
              <ul>
                <Guideline
                  type="do"
                  title={t("brand.guidelines.do.officialAssets.title")}
                  description={t("brand.guidelines.do.officialAssets.desc")}
                />
                <Guideline
                  type="do"
                  title={t("brand.guidelines.do.clearSpace.title")}
                  description={t("brand.guidelines.do.clearSpace.desc")}
                />
                <Guideline
                  type="do"
                  title={t("brand.guidelines.do.highRes.title")}
                  description={t("brand.guidelines.do.highRes.desc")}
                />
                <Guideline
                  type="do"
                  title={t("brand.guidelines.do.colors.title")}
                  description={t("brand.guidelines.do.colors.desc")}
                />
              </ul>
            </div>
            <div>
              <h3>{t("brand.guidelines.donts.h3")}</h3>
              <ul>
                <Guideline
                  type="dont"
                  title={t("brand.guidelines.dont.modify.title")}
                  description={t("brand.guidelines.dont.modify.desc")}
                />
                <Guideline
                  type="dont"
                  title={t("brand.guidelines.dont.colors.title")}
                  description={t("brand.guidelines.dont.colors.desc")}
                />
                <Guideline
                  type="dont"
                  title={t("brand.guidelines.dont.background.title")}
                  description={t("brand.guidelines.dont.background.desc")}
                />
                <Guideline
                  type="dont"
                  title={t("brand.guidelines.dont.lowQuality.title")}
                  description={t("brand.guidelines.dont.lowQuality.desc")}
                />
              </ul>
            </div>
          </div>
        </section>

        <section
          className="company-container brand-contact"
          aria-labelledby="brand-contact-title"
        >
          <div>
            <h2 id="brand-contact-title">{t("brand.contact.h2")}</h2>
            <p>{t("brand.contact.body")}</p>
          </div>
          <div className="company-actions">
            <a href={contactUrl} className="company-button">
              <Send size={18} aria-hidden="true" />
              {t("brand.contact.telegram")}
            </a>
            <a
              href={siteXUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="company-text-link"
            >
              {t("brand.contact.twitter")}
            </a>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
